import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, ChevronRight, X, Building2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { categories } from "../mock";
import ProductCard from "../components/ProductCard";
import API from "../api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";

const Catalog = () => {
  const { cat } = useParams();
  const { t, tl } = useApp();
  const [searchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").toLowerCase();
  const saleOnly = searchParams.get("sale") === "1";

  const [allProducts, setAllProducts] = useState([]);
  const [selectedCat, setSelectedCat] = useState(cat === "all" ? "all" : cat);
  const [maxPrice, setMaxPrice] = useState(800);
  const [sort, setSort] = useState("popular");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { API.get("/products").then((r) => setAllProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => { setSelectedCat(cat === "all" ? "all" : cat); }, [cat]);

  const filtered = useMemo(() => {
    let list = [...allProducts];
    if (selectedCat !== "all") list = list.filter((p) => p.category === selectedCat);
    if (saleOnly) list = list.filter((p) => p.oldPrice);
    if (query) list = list.filter((p) => tl(p.name).toLowerCase().includes(query) || p.brand.toLowerCase().includes(query));
    list = list.filter((p) => p.price <= maxPrice);
    switch (sort) {
      case "priceAsc": list.sort((a, b) => a.price - b.price); break;
      case "priceDesc": list.sort((a, b) => b.price - a.price); break;
      case "new": list.sort((a, b) => Number(b.isNew) - Number(a.isNew)); break;
      default: list.sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [allProducts, selectedCat, maxPrice, sort, query, saleOnly, tl]);

  const title = saleOnly ? t("sale") : query ? `"${searchParams.get("q")}"` : selectedCat === "all" ? t("newArrivals") : t(categories.find((c) => c.id === selectedCat)?.key || "all");

  const FilterPanel = (
    <div className="space-y-8">
      <div>
        <h4 className="text-xs uppercase tracking-[0.15em] text-[#1b1b1b] mb-4">{t("category")}</h4>
        <ul className="space-y-2.5">
          <li><button onClick={() => setSelectedCat("all")} className={`text-sm transition-colors ${selectedCat === "all" ? "text-[#b08d57] font-medium" : "text-[#555] hover:text-[#1b1b1b]"}`}>{t("all")}</button></li>
          {categories.map((c) => (<li key={c.id}><button onClick={() => setSelectedCat(c.id)} className={`text-sm transition-colors ${selectedCat === c.id ? "text-[#b08d57] font-medium" : "text-[#555] hover:text-[#1b1b1b]"}`}>{t(c.key)}</button></li>))}
        </ul>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.15em] text-[#1b1b1b] mb-4">{t("priceRange")}</h4>
        <Slider value={[maxPrice]} onValueChange={(v) => setMaxPrice(v[0])} min={50} max={800} step={10} className="mt-2" />
        <div className="flex justify-between mt-3 text-sm text-[#555]"><span>50 €</span><span className="text-[#1b1b1b] font-medium">≤ {maxPrice} €</span></div>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 fade-up">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-5">
        <Link to="/" className="hover:text-[#b08d57]">{t("home")}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-[#1b1b1b]">{title}</span>
      </div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="h-px w-10 bg-[#b08d57] mb-4" />
          <h1 className="font-serif-lux text-4xl md:text-5xl text-[#1b1b1b]">{title}</h1>
          <p className="text-sm text-muted-foreground mt-2">{filtered.length} {t("products")}</p>
        </div>
      </div>
      {selectedCat === "negoce" && (
        <div className="mb-8 flex items-center gap-3 bg-[#1b1b1b] text-white px-5 py-4 rounded-sm" data-testid="b2b-banner">
          <Building2 className="h-5 w-5 text-[#b08d57] shrink-0" strokeWidth={1.5} />
          <p className="text-sm">{t("b2bBanner")}</p>
        </div>
      )}
      <div className="grid md:grid-cols-[220px_1fr] gap-8">
        <aside className="hidden md:block">{FilterPanel}</aside>
        <div>
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setShowFilters(true)} className="md:hidden flex items-center gap-2 text-sm border border-border px-4 py-2.5 rounded-sm"><SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} /> {t("filters")}</button>
            <span className="hidden md:block" />
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground hidden sm:inline">{t("sortBy")}</span>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[180px] bg-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="popular">{t("sortPopular")}</SelectItem>
                  <SelectItem value="new">{t("sortNew")}</SelectItem>
                  <SelectItem value="priceAsc">{t("sortPriceAsc")}</SelectItem>
                  <SelectItem value="priceDesc">{t("sortPriceDesc")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="py-24 text-center text-muted-foreground"><p>Aucun article trouvé.</p></div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-8">{filtered.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
          )}
        </div>
      </div>
      {showFilters && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-[300px] bg-[#faf8f5] p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6"><span className="font-serif-lux text-2xl">{t("filters")}</span><button onClick={() => setShowFilters(false)}><X className="h-5 w-5" /></button></div>
            {FilterPanel}
            <button onClick={() => setShowFilters(false)} className="mt-8 w-full bg-[#1b1b1b] text-white py-3 text-sm tracking-wide">{t("viewAll")} ({filtered.length})</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalog;
